/**
 * For backward compability, this is used as it is only the duration
 * from IServiceTypeBFReact that is actually used here.
 * Using IServiceTypeBFReact assumes waaay to much about where and when
 */

import {Period, RRDate} from "../RRDate";
import type {RRDuration} from "../RRDuration/Duration";

/**
 * 0-59
 */
type Minute = number;

type Minutes = number;
type Millis = number;
type MilliPeriod = {from: Millis; to: Millis};
type MilliDuration = Millis;

export const trimToIntervals = (period: MilliPeriod, lengthOfIntervals: Minutes, startingMinuteOfFirstHour: Minute): MilliPeriod => {
  const h = 60 * 60 * 1000;
  const m = 60 * 1000;
  const firstHourOfPeriod = Math.floor(period.from / h) * h;
  const gridStart = firstHourOfPeriod + startingMinuteOfFirstHour * m;
  const offSetOfPeriodFromAndGrid =
    period.from - gridStart === 0 || (period.from - gridStart) % (lengthOfIntervals * m) === 0
      ? 0
      : Math.abs(((period.from - gridStart) % (lengthOfIntervals * m)) - lengthOfIntervals * m);
  const start = period.from + offSetOfPeriodFromAndGrid;
  const modulusOfEndOffsetAndGridSize = (period.to - gridStart) % (lengthOfIntervals * m);
  const end = period.to - modulusOfEndOffsetAndGridSize;
  return {from: start, to: end};
};

/**
 * This service returns the offered timeslots,
 *  that the user can select to be booked,
 *  based on a number of provided bookable (true) AVs.
 * We assume:
 * - the user has already picked a ServiceType (duration!), a Partner, and a ServiceDate
 *
 * @param bookableSchedulesOfADay Array of bookable / truely available schedules.
 * @param serviceType The selected servicetype
 * @returns Timeslots
 */
export const getOfferedTimeSlots = (bookableSchedulesOfADay: Period[], duration: RRDuration): Period[] => {
  const bookableSchedulesOfADayMillis = bookableSchedulesOfADay.map((bs) => {
    return {
      from: bs.from.toMillis(),
      to: bs.to.toMillis(),
    };
  });
  const durationInMillis = duration.toMillis();

  // Triming Schedule-Edges to Intervals
  const bookableSchedules = bookableSchedulesOfADayMillis.map((s) => {
    return trimToIntervals(s, 5, 0);
  });

  // Spliting Schedules into Time Slots
  const timeSlots: MilliPeriod[] = [];
  bookableSchedules.forEach((schedule) => {
    const timeSlotsOfThisSchedule = splitScheduleIntoTimeSlots(schedule, durationInMillis);
    timeSlotsOfThisSchedule.forEach((t) => timeSlots.push(t));
  });
  // ordering the result
  const result = timeSlots.sort((a, b) => {
    return a.from - b.from;
  });

  const mapped = result.map((r) => {
    const start = RRDate.fromMillis(r.from);
    const end = RRDate.fromMillis(r.to);
    return new Period(start, end);
  });
  return mapped;
};

/**
 * This function returns timeslots from a given timeframe, based on the business logic applied.
 * (For example: we always want to display those X number of timeslots from a given timeframe,
 * following the selection rules below...)
 * @param period to make timeslots from
 * @param duration which each timeslot should at least fit into
 * @returns timeslots
 */
export const splitScheduleIntoTimeSlots = (period: MilliPeriod, durationOfService: MilliDuration): MilliPeriod[] => {
  const list: Set<string> = new Set();
  const serviceMillis = Math.floor(durationOfService / 1000 / 60) * 1000 * 60;
  const periodMillis = Math.floor((period.to - period.from) / 1000 / 60) * 1000 * 60;
  for (let front = 0; front <= periodMillis - serviceMillis; front += serviceMillis) {
    const timeSlotFrom = period.from + front;
    const timeSlotTo = period.from + front + serviceMillis;
    const timeSlot = {from: timeSlotFrom, to: timeSlotTo};
    list.add(JSON.stringify(timeSlot));
  }
  for (let end = periodMillis - serviceMillis; end > 0; end -= serviceMillis) {
    const timeSlotFrom = period.from + end;
    const timeSlotTo = period.from + end + serviceMillis;
    const timeSlot = {from: timeSlotFrom, to: timeSlotTo};
    list.add(JSON.stringify(timeSlot));
  }
  const result: MilliPeriod[] = [];
  list.forEach((t) => result.push(JSON.parse(t)));
  return result;
};
